import { Sliders } from "@phosphor-icons/react"
import { useFlags } from "~/features/feature-flags"
import { useMemo } from "react"

import { AdjustmentManagerDialog } from "~/components/forecast/dialogs/AdjustmentManagerDialog"
import { RemoveVariantsFromCollectionDialog } from "~/components/collections/dialogs/RemoveVariantsFromCollectionDialog"
import { PurchaseOrderDialog } from "~/components/purchaseOrder/PurchaseOrderDialog"
import Commandbar from "~/components/planning/Commandbar"
import MultiEditDialog from "~/components/planning/MultiEditDialog/MultiEditDialog"
import { EditSkuTagsDialog } from "~/components/planning/Tags/EditSkuTagsDialog"
import { Button } from "~/components/reusable/Button"
import { useCollectionContext } from "~/context/CollectionProvider"
import { useVariantsByIds } from "~/hooks/useVariants"
import { getCommonVendors } from "~/utils/vendorUtils"
import type { IVariantSummary } from "~/hooks/useVariantTableData"

type Props = {
  selectedVariantIds: string[]
  selectedVariantCount: number
  clearSelectedVariantIds: () => void
}

export function NoosDemandPlanCommandBar({
  selectedVariantIds,
  selectedVariantCount,
  clearSelectedVariantIds,
}: Props) {
  const { adjustments: adjustmentsFlag } = useFlags()
  const variants = useVariantsByIds(selectedVariantIds)
  const skuIds = useMemo(() => variants.flatMap((v: IVariantSummary) => v.skuIds), [variants])
  const commonVendors = useMemo(() => getCommonVendors(variants), [variants])

  const { collections } = useCollectionContext()
  const collectionId = collections.length === 1 ? collections[0]!.id : undefined
  const removeDisabled = collectionId === undefined
  const removeDisabledTooltip = removeDisabled ? "Cannot remove from a collection group" : undefined

  return (
    <Commandbar count={selectedVariantCount} onClear={clearSelectedVariantIds}>
      <MultiEditDialog
        selectedSkuIds={skuIds}
        selectedVariantCount={selectedVariantCount}
        resetSelectedProductIds={clearSelectedVariantIds}
        commonVendors={commonVendors}
      />
      {adjustmentsFlag && (
        <AdjustmentManagerDialog variantIds={selectedVariantIds} allVariants={variants}>
          <Button asChild variant="neutral" size="sm">
            <Sliders />
            Adjust
          </Button>
        </AdjustmentManagerDialog>
      )}
      <PurchaseOrderDialog variants={variants} />
      <EditSkuTagsDialog variantIds={selectedVariantIds} />
      <RemoveVariantsFromCollectionDialog
        variantIds={selectedVariantIds}
        collectionId={collectionId ?? 0}
        onSuccess={clearSelectedVariantIds}
        disabled={removeDisabled}
        disabledTooltip={removeDisabledTooltip}
      />
    </Commandbar>
  )
}
